import type { Maybe } from '@adornis/base/utilTypes.js';

/**
 * Everything a mail local part and a group's `mailNickname` are allowed to contain.
 *
 * ! A whitelist, not a list of characters we happened to think of. !
 *
 * This used to strip whitespace and the five German umlauts and let everything else through. A group
 * called "Klassenfahrt Harz 2026 10A, A10B, 10C" therefore reached Entra as
 * `KlassenfahrtHarz202610A,A10B,10C` and was rejected - commas are among the many characters Entra
 * forbids there, and so are `@ ( ) \ [ ] " ; : < >` and every accent anybody ever pastes out of Word.
 * Naming the characters that are *allowed* is the only version of this that cannot be surprised.
 */
const ALLOWED_CHARACTERS = /[^A-Za-z0-9._-]/g;

/** German characters that have an established spelling rather than a sensible transliteration. */
const TRANSLITERATIONS: Array<[RegExp, string]> = [
  [/ß/g, 'ss'],
  [/ä/g, 'ae'],
  [/ö/g, 'oe'],
  [/ü/g, 'ue'],
  [/Ä/g, 'Ae'],
  [/Ö/g, 'Oe'],
  [/Ü/g, 'Ue'],
];

/**
 * Turns a name into something usable as the local part of a mail address.
 *
 * German umlauts are spelled out ("Müller" -> "Mueller") because that is how people expect their own
 * name to look; every remaining accent is reduced to its base letter, and anything still outside
 * {@link ALLOWED_CHARACTERS} is dropped.
 * @param str the raw name
 */
export function cleanStringForEmailUse(str: string): string;
export function cleanStringForEmailUse(str: undefined | null): undefined;
export function cleanStringForEmailUse(str: Maybe<string>): string | undefined;
export function cleanStringForEmailUse(str: Maybe<string>): string | undefined {
  if (str === null || str === undefined) return undefined;

  const transliterated = TRANSLITERATIONS.reduce(
    (value, [pattern, replacement]) => value.replace(pattern, replacement),
    str.trim(),
  );

  return (
    transliterated
      // é -> e, ç -> c. Decomposing first turns the accent into its own code point, which the
      // whitelist below then drops - so an accented name keeps its letters instead of losing them.
      .normalize('NFD')
      .replace(ALLOWED_CHARACTERS, '')
  );
}
